import { ApiError } from '@/lib/api/errors'
import { api } from '@/lib/api/requests'
import { captureIfDeletedPending, DeletedAccountPendingSignal } from '@/lib/auth/deletedAccount'
import type { SessionTokens } from '@/lib/auth/tokenStore'

type LoginWithPasswordPayload = {
  email: string
  password: string
}

type NewPasswordChallengePayload = {
  email: string
  session: string
  password: string
}

type TwoFactorCodePayload = {
  email: string
  session: string
  code: string
}

type SetPasswordPayload = {
  token: string
  password: string
}

type LoginWithPasswordResponse = {
  accessToken?: string
  idToken?: string
  refreshToken?: string
  tokenType?: string
  expiresIn?: number
  /**
   * A CORRECT password can make /auth/login answer `200 { challenge, session }`
   * INSTEAD of tokens: `NEW_PASSWORD_REQUIRED` for an invited account (backend
   * 3487edf4, answered at /auth/login/new-password) and `SOFTWARE_TOKEN_MFA`
   * for an account with two-factor on (backend a582d1e0, answered at
   * /auth/login/code). No cookie is set and nobody is signed in yet; the
   * session is a 3-minute single-use credential.
   */
  challenge?: string
  session?: string
}

type RefreshSessionResponse = {
  accessToken?: string
  idToken?: string
  tokenType?: string
  expiresIn?: number
}

type ConfirmPasswordResetPayload = {
  email: string
  code: string
  newPassword: string
}

function mapTokensFromResponse(payload: LoginWithPasswordResponse | RefreshSessionResponse): SessionTokens {
  const expiresIn = Number(payload?.expiresIn)
  const expiresAt = Number.isFinite(expiresIn) && expiresIn > 0 ? Date.now() + expiresIn * 1000 : undefined
  return {
    accessToken: payload?.accessToken,
    idToken: payload?.idToken,
    tokenType: payload?.tokenType,
    expiresAt,
  }
}

/** Step 1 answered with a challenge: no tokens exist and nobody is signed in. */
export type PasswordLoginChallenge = { challenge: string; session: string }
export type PasswordLoginOutcome = PasswordLoginChallenge | { tokens: SessionTokens }

export function isPasswordLoginChallenge(outcome: PasswordLoginOutcome): outcome is PasswordLoginChallenge {
  return 'challenge' in outcome
}

/**
 * Decides what a sign-in response actually is, and is the guard this whole
 * feature rests on: a challenge body must NEVER reach mapTokensFromResponse.
 * That mapper does not throw on a body without tokens — it returns
 * `{ accessToken: undefined }`, which stores as a session, navigates into the
 * app and 401s every call afterwards. Exported so a test can hold it to that.
 */
export function readPasswordLoginOutcome(
  response: LoginWithPasswordResponse | null | undefined,
): PasswordLoginOutcome {
  const challenge = String(response?.challenge || '').trim()
  if (challenge) return { challenge, session: String(response?.session || '') }
  return { tokens: mapTokensFromResponse(response || {}) }
}

export async function loginWithPassword(payload: LoginWithPasswordPayload): Promise<PasswordLoginOutcome> {
  const response = await api.post<LoginWithPasswordResponse>('/auth/login', {
    auth: 'omit',
    body: payload,
    handleAuthErrors: false,
  })
  // Deleted account inside its 30-day window → no session; route to the choice
  // screen (Restore or New) instead of completing sign-in.
  if (captureIfDeletedPending(response)) throw new DeletedAccountPendingSignal()
  return readPasswordLoginOutcome(response)
}

/**
 * What a step-2 door answered, read the way /auth/login's answer is: a
 * soft-deleted account routes to its choice screen, and tokens become a session.
 * Neither door has a 200-with-challenge in its contract. If one ever appears it
 * is refused as `signin_failed` rather than stored as an empty token set, so
 * nobody is ever signed in tokenless. Exported so a test can hold both doors to it.
 */
export function readChallengeAnswer(response: LoginWithPasswordResponse | null | undefined): SessionTokens {
  if (captureIfDeletedPending(response)) throw new DeletedAccountPendingSignal()
  const outcome = readPasswordLoginOutcome(response)
  if (isPasswordLoginChallenge(outcome)) {
    throw new ApiError(500, 'Sign in could not be completed.', { error_code: 'signin_failed' })
  }
  return outcome.tokens
}

/**
 * Step 2 of an invited sign-in: answers the NEW_PASSWORD_REQUIRED challenge
 * that /auth/login handed back. Public (no auth header) — the short-lived
 * challenge session IS the auth, so it travels in the body of this one request
 * and is held nowhere else. On success the server issues a session through the
 * same buildSessionResponse helper as /auth/login (token body + HttpOnly
 * tc_refresh cookie), so the deleted_pending branch behaves identically.
 * Failures surface as ApiError with `payload.error_code` (mapped in
 * pages/auth/newPasswordChallengeErrors, where `signin_failed` means the password
 * is already changed, so "sign in to continue" is the honest exit).
 */
export async function completeNewPasswordChallenge(
  payload: NewPasswordChallengePayload,
): Promise<SessionTokens> {
  const response = await api.post<LoginWithPasswordResponse>('/auth/login/new-password', {
    auth: 'omit',
    body: payload,
    handleAuthErrors: false,
  })
  return readChallengeAnswer(response)
}

/**
 * Step 2 of a sign-in with two-factor on: answers the SOFTWARE_TOKEN_MFA
 * challenge /auth/login handed back with the authenticator's 6-digit code
 * (backend a582d1e0). Public, like the new-password door: the challenge session
 * IS the auth and travels in this one request body only. The session comes from
 * the same buildSessionResponse as every other way in, so the tokens, the
 * tc_refresh cookie and the deleted_pending branch are identical. Failures
 * surface as ApiError with `payload.error_code` (mapped in
 * pages/auth/codeChallengeErrors).
 */
export async function completeTwoFactorChallenge(payload: TwoFactorCodePayload): Promise<SessionTokens> {
  const response = await api.post<LoginWithPasswordResponse>('/auth/login/code', {
    auth: 'omit',
    body: payload,
    handleAuthErrors: false,
  })
  return readChallengeAnswer(response)
}

/**
 * Activates an account via a one-time set-password token from an email link.
 * Public (no auth header). On success the server issues a session identical to
 * /auth/login (token body + HttpOnly tc_refresh cookie). Failures surface as
 * ApiError with `payload.error_code` (mapped in pages/auth/setPasswordErrors).
 */
export async function setPasswordWithToken(payload: SetPasswordPayload): Promise<SessionTokens> {
  const response = await api.post<LoginWithPasswordResponse>('/auth/set-password', {
    auth: 'omit',
    body: payload,
    handleAuthErrors: false,
  })
  if (captureIfDeletedPending(response)) throw new DeletedAccountPendingSignal()
  return mapTokensFromResponse(response || {})
}

/**
 * Exchanges the HttpOnly refresh-cookie for a fresh access+id token pair.
 * Returns null if no valid session exists (401). Never throws on auth failure;
 * other errors propagate.
 */
export async function refreshSession(): Promise<SessionTokens | null> {
  try {
    const response = await api.post<RefreshSessionResponse>('/auth/refresh', {
      auth: 'omit',
      handleAuthErrors: false,
    })
    if (!response?.accessToken) return null
    return mapTokensFromResponse(response)
  } catch (error) {
    const status = (error as { status?: number } | null)?.status
    if (status === 401) return null
    throw error
  }
}

/**
 * Clears the server-side refresh cookie + revokes refresh tokens via Cognito
 * GlobalSignOut. Best-effort — never throws, since the client is leaving the
 * session anyway.
 */
export async function logoutSession(): Promise<void> {
  try {
    await api.post<{ ok?: boolean }>('/auth/logout', {
      auth: 'include',
      handleAuthErrors: false,
    })
  } catch {
    // Silent — frontend logout proceeds regardless.
  }
}

export async function requestPasswordReset(email: string): Promise<void> {
  await api.post<{ ok?: boolean }>('/auth/forgot-password', {
    auth: 'omit',
    body: { email },
    handleAuthErrors: false,
  })
}

export async function confirmPasswordReset(payload: ConfirmPasswordResetPayload): Promise<void> {
  await api.post<{ ok?: boolean }>('/auth/forgot-password/confirm', {
    auth: 'omit',
    body: payload,
    handleAuthErrors: false,
  })
}

type ConfirmAccountDeletionResponse = {
  ok?: boolean
  deleted?: boolean
  status?: string
}

/**
 * Confirms passwordless (SSO) account deletion via a one-time token from an
 * email link (RTBF / D1.12). Public (no auth header); the token IS the auth.
 * On success the backend soft-deletes and clears the refresh cookie. Any
 * bad / expired / used / missing token returns a generic 400 invalid_token
 * (mapped in pages/auth/deleteAccountErrors).
 */
export async function confirmAccountDeletion(token: string): Promise<ConfirmAccountDeletionResponse> {
  return api.post<ConfirmAccountDeletionResponse>('/auth/delete-account/confirm', {
    auth: 'omit',
    body: { token },
    handleAuthErrors: false,
  })
}

type AccountActionResponse = { status?: string }

/**
 * Restore a soft-deleted account within its 30-day window (ADR
 * adr-account-deletion-recreation). Authed by the deleted_pending access token
 * (no stored session yet); the server clears the tombstone and sets the refresh
 * cookie, so the caller hydrates the session via /auth/refresh afterwards.
 */
export async function restoreAccount(
  accessToken: string,
  refreshToken: string,
): Promise<AccountActionResponse> {
  return api.post<AccountActionResponse>('/me/account/restore', {
    auth: 'omit',
    headers: { Authorization: `Bearer ${accessToken}` },
    body: { refreshToken },
    handleAuthErrors: false,
  })
}

/**
 * Discard a soft-deleted account: hard-delete it now + free the email/phone
 * uniques, so the user can re-register fresh on the same email.
 */
export async function discardAccount(accessToken: string): Promise<AccountActionResponse> {
  return api.post<AccountActionResponse>('/me/account/discard', {
    auth: 'omit',
    headers: { Authorization: `Bearer ${accessToken}` },
    handleAuthErrors: false,
  })
}
